import { API_BASE_URL } from "config";

import {
  BatchStatsRequest,
  BatchStatsResponse,
  ContributorStats,
  GlobalStats,
  ListCommentsResponse,
  PostCommentInput,
  UseCaseComment,
  UseCaseUsageStats,
} from "../models/user-insights-models";
import { BaseService } from "./base-service";

/**
 * Client for use-case interaction endpoints on ITShopUserInsightsService.
 * Base path: /usecases
 */
export class UserInsightsService extends BaseService {
  constructor() {
    super(API_BASE_URL);
  }

  // ── Stats ──────────────────────────────────────────────────────────────

  /** Get stats for a single use case. */
  public async getStats(useCaseId: string): Promise<UseCaseUsageStats> {
    const response = await this.fetch(`/telemetry/usecases/${encodeURIComponent(useCaseId)}/stats`);
    if (!response.ok) throw new Error(`Failed to fetch stats: ${response.status}`);
    const body = await response.json();
    return body.stats ?? body;
  }

  /** Get stats for multiple use cases in one call. */
  public async getBatchStats(useCaseIds: string[]): Promise<BatchStatsResponse> {
    const response = await this.fetch("/telemetry/usecases/batch-stats", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ useCaseIds } as BatchStatsRequest),
    });
    if (!response.ok) throw new Error(`Failed to fetch batch stats: ${response.status}`);
    const body = await response.json();
    return { stats: body.stats ?? body };
  }

  /** Get global community stats (total use cases, contributors, tools). */
  public async getGlobalStats(): Promise<GlobalStats> {
    const response = await this.fetch("/telemetry/usecases/global-stats");
    if (!response.ok) throw new Error(`Failed to fetch global stats: ${response.status}`);
    const body = await response.json();
    const stats = body.stats ?? body;
    return {
      totalUseCases: stats.totalUseCases ?? 0,
      totalContributors: stats.totalContributors ?? 0,
      totalAiTools: stats.totalToolsReferenced ?? stats.totalAiTools ?? 0,
    };
  }

  // ── Interactions ───────────────────────────────────────────────────────

  /** Record that the current user viewed this use case. */
  public async recordView(useCaseId: string, csrfToken: string): Promise<UseCaseUsageStats> {
    const response = await this.fetch(`/telemetry/usecases/${encodeURIComponent(useCaseId)}/view`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": csrfToken },
    });
    if (!response.ok) throw new Error(`Failed to record view: ${response.status}`);
    const body = await response.json();
    return body.stats ?? body;
  }

  /** Record "I used this". Idempotent per user. */
  public async recordUsed(useCaseId: string, csrfToken: string): Promise<UseCaseUsageStats> {
    const response = await this.fetch(`/telemetry/usecases/${encodeURIComponent(useCaseId)}/used`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": csrfToken },
    });
    if (!response.ok) throw new Error(`Failed to record used: ${response.status}`);
    const body = await response.json();
    return body.stats ?? body;
  }

  // ── Contributor dashboard ──────────────────────────────────────────────

  /** Get aggregate stats for a contributor. */
  public async getContributorStats(alias: string, csrfToken: string): Promise<ContributorStats> {
    const response = await this.fetch(`/telemetry/usecases/contributors/${alias}/stats`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": csrfToken },
    });
    if (!response.ok) throw new Error(`Failed to fetch contributor stats: ${response.status}`);
    return response.json();
  }

  // ── Comments ───────────────────────────────────────────────────────────

  /** List comments for a use case. */
  public async listComments(useCaseId: string, nextToken?: string): Promise<ListCommentsResponse> {
    const params = new URLSearchParams();
    if (nextToken) params.set("nextToken", nextToken);
    const qs = params.toString();
    const response = await this.fetch(`/usecases/${encodeURIComponent(useCaseId)}/comments${qs ? `?${qs}` : ""}`);
    if (!response.ok) throw new Error(`Failed to list comments: ${response.status}`);
    return response.json();
  }

  /** Post a comment or reply. */
  public async postComment(useCaseId: string, input: PostCommentInput, csrfToken: string): Promise<UseCaseComment> {
    const response = await this.fetch(`/usecases/${encodeURIComponent(useCaseId)}/comments`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": csrfToken },
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new Error(`Failed to post comment: ${response.status}`);
    const body = await response.json();
    return body.comment ?? body;
  }
}
